/**
 * Hand-written shapes for the Playbooks API. Keep in sync with Pydantic
 * schemas in `knitknot/api/models/playbook*.py` and `routers/playbooks.py`.
 */

import type { PlaybookDemand, PlaybookProvenance } from '@knitknot/shared/components/report';
import type { EvidenceReachBasis } from '@/types/issues';

// Dead artifact shapes (third_party, competitor_mirror, revise_existing_page,
// refresh) were deleted in the taxonomy cutover — historical rows are retyped
// server-side by the Phase-1 migration, so no live payload carries them.
export const PLAYBOOK_TYPES = [
  'comparison_page',
  'feature_page',
  'correction',
  'icp_content',
  'brand_attribution',
  'site_fix',
  'earned_mention',
  'markdown_page',
] as const;
export type PlaybookType = (typeof PLAYBOOK_TYPES)[number];

export const PLAYBOOK_STATUSES = ['proposed', 'approved', 'in_progress', 'shipped', 'measured', 'dismissed'] as const;
export type PlaybookStatus = (typeof PLAYBOOK_STATUSES)[number];

export const PLAYBOOK_PRIORITIES = ['high', 'medium', 'low'] as const;
export type PlaybookPriority = (typeof PLAYBOOK_PRIORITIES)[number];

export const PLAYBOOK_FAMILIES = ['discovery', 'competitive', 'technical', 'markdown'] as const;
export type PlaybookFamily = (typeof PLAYBOOK_FAMILIES)[number];

export interface JudgeQuote {
  engine: string;
  prompt_id: string;
  run_prompt_id: string;
  quote: string;
  sentiment?: string | null;
}

/**
 * An enriched cited source — the SPECIFIC page AI read, not a bare domain.
 * Emitted by the backend cited-sources enrichment (additive; older payloads
 * carry only `evidence.domains: string[]`, so the detail page degrades to those
 * when `cited_sources` is absent). `owner_type` is one of `us | competitor |
 * third_party` (mirrors source_owner_type), used for the ownership badge.
 */
export interface PlaybookCitedSource {
  url: string;
  domain: string | null;
  title?: string | null;
  owner_type?: string | null;
  owner_name?: string | null;
  /** Times this exact page was cited across the playbook's evidence. */
  count?: number | null;
  /** The page's published / last-fresh date (ISO). Null when un-fetched.
   *  A stale date ("AI cites a 2021 post") is a strong sales signal. */
  published_at?: string | null;
}

export interface PlaybookEvidence {
  workspace_prompt_ids: string[];
  run_prompt_ids: string[];
  engines: string[];
  judge_quotes: JudgeQuote[];
  feature_names: string[];
  competitor_names: string[];
  domains: string[];
  /** Enriched per-page cited sources (url + domain + owner). Optional —
   *  present once the backend cited-sources enrichment ships. */
  cited_sources?: PlaybookCitedSource[] | null;
  icp_personas: string[];
  n_losing_evals: number;
  search_volume_sum: number;
  avg_cpc: number | null;
  max_importance: number;
  avg_importance: number;
  high_importance_count: number;
}

// The block registry's fallback renderer handles unknown ``kind`` values —
// the backend can ship a new block kind before the frontend learns about it.
export interface BlockBase {
  kind: string;
  version: number;
}
export interface TargetUrlBlock extends BlockBase {
  kind: 'target_url';
  url: string;
  rationale: string;
}
export interface KeyPoint {
  text: string;
  rationale?: string | null;
}
export interface KeyPointsBlock extends BlockBase {
  kind: 'key_points';
  points: KeyPoint[];
}
export interface SuggestedAuthorBlock extends BlockBase {
  kind: 'suggested_author';
  name: string;
  reason: string;
}
export interface TargetDateBlock extends BlockBase {
  kind: 'target_date';
  date_iso: string;
  rationale: string;
}
export interface InternalLink {
  from_url: string;
  anchor_text: string;
  to_url: string;
}
export interface InternalLinksBlock extends BlockBase {
  kind: 'internal_links';
  links: InternalLink[];
}
export interface WhereToPostBlock extends BlockBase {
  kind: 'where_to_post';
  primary: string;
  secondary: string[];
  notes?: string | null;
}
export type ArtifactBlock =
  | TargetUrlBlock
  | KeyPointsBlock
  | SuggestedAuthorBlock
  | TargetDateBlock
  | InternalLinksBlock
  | WhereToPostBlock
  | BlockBase;

export interface PlaybookArtifact {
  blocks: ArtifactBlock[];
}

export interface ShippedUrlCheck {
  status: 'verified' | 'unchanged' | 'no_match' | 'same_as_homepage' | 'deferred' | 'unreachable';
  http_status: number | null;
  matched_terms: string[];
  missing_terms: string[];
  final_url: string | null;
  checked_at: string | null;
  error: string | null;
}

/** Provenance of a ship the DETECTOR made: the customer published a page, we
 *  matched it to this play and marked it done on their behalf
 *  (knitknot/core/playbooks/ship_detection.py). Absent when a human pasted the
 *  URL — the console must never present an inference as an attestation. */
export interface ShipDetection {
  detected_at: string;
  detector: string;
  url: string;
  page_title: string | null;
  page_published_at: string | null;
  method: 'exact_url' | 'slug_exact' | 'title_exact' | 'fuzzy' | (string & {});
  score: number;
  matched_tokens: string[];
  distinctive_tokens: string[];
  revived_from_superseded: boolean;
}

/** The five play families (taxonomy cutover, D6): create a new page, promote
 *  an existing page AI ignores into the source set, reinforce a page AI already
 *  cites, deterministically fix a mechanical GEO blocker, or earn a change on a
 *  third-party source. The retired legacy `revise`/`defend` verbs may transiently
 *  appear from old data during the deploy window — readers NEVER crash on
 *  them; `playbookVerbLabel` falls back to the capitalized raw verb. */
export type PlaybookVerb = 'create' | 'promote' | 'reinforce' | 'fix' | 'earn';

export const PLAYBOOK_VERBS: readonly PlaybookVerb[] = ['create', 'promote', 'reinforce', 'fix', 'earn'];

/** Deploy-window fallback ONLY — the server's `verb_info.verb_label` is the
 *  single source of naming truth; render from it when present. */
export const PLAYBOOK_VERB_LABELS: Record<PlaybookVerb, string> = {
  create: 'Create',
  promote: 'Promote',
  reinforce: 'Reinforce',
  fix: 'Site fix',
  earn: 'Earn',
};

/** Wire verb — the canonical union plus tolerance for legacy strings
 *  (`revise`/`defend`, legacy) an old row may briefly carry during the deploy window. */
export type PlaybookVerbWire = PlaybookVerb | (string & {});

/** Verb label with the legacy fallback: `verb_info.verb_label` wins, then the
 *  local map, then the capitalized raw verb (a legacy `revise`/`defend` row
 *  renders "Revise"/"Defend", never blank, never a crash). */
export function playbookVerbLabel(
  verb: PlaybookVerbWire | null | undefined,
  verbInfo?: PlaybookVerbInfo | null,
): string | null {
  if (verbInfo?.verb_label) return verbInfo.verb_label;
  if (!verb) return null;
  const known = (PLAYBOOK_VERB_LABELS as Record<string, string>)[verb];
  if (known) return known;
  const raw = verb.trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : null;
}

/** How a play is measured — drives the Tracking tab's branch. */
export type MeasurementRecipe = 'prompts' | 'citation' | 'issue_recheck' | 'source_change';

/** The backend verb registry's per-verb contract, served on list + detail
 *  responses. THE source of naming/methodology truth — local maps are
 *  deploy-window fallbacks only. */
export interface PlaybookVerbInfo {
  verb_label: string;
  methodology_blurb: string;
  target_semantics: string;
  measurement_recipe: MeasurementRecipe | (string & {});
  runs_to_signal?: number | null;
  weeks_range?: [number, number] | null;
  expectation_copy: string;
}

/** Provenance tag (the origin field) — why the play was minted. `defend` = the erosion trigger
 *  minted it (renders the quiet "Defensive" tag); absent otherwise. */
export type PlaybookOrigin = 'defend' | (string & {});

/** Measured age-at-citation window (D5) — the empirical overlay next to the
 *  benchmark priors ("Pages AI cites in your space: median N weeks old"). */
export interface PlaybookMeasuredWindow {
  median_weeks: number;
  p25_weeks?: number | null;
  p75_weeks?: number | null;
  n: number;
  undated_share?: number | null;
  cohort?: string | null;
  scope?: string | null;
}

/** W2-E honesty label on every tracked-prompts payload (REST + MCP, same
 *  module): `baseline` = the play has NOT shipped, so the rows are what AI
 *  answers TODAY for the targeted questions — never results; `measuring` =
 *  shipped/measured, impact is judged against the frozen at-ship cohort. */
export type TrackingPhase = 'baseline' | 'measuring';

/** Detail-only tracking contract: the resolved recipe plus a structured reason
 *  when the prompt resolver returned empty — rendered as CAUSAL empty-state
 *  copy, never a bare "No tracked prompts yet". */
export interface PlaybookTracking {
  recipe: MeasurementRecipe | (string & {});
  reason: { code: string; message: string } | null;
  /** Absent on payloads that predate W2-E — fall back to the status bucket. */
  phase?: TrackingPhase | (string & {}) | null;
}

/** Per-row at-ship fields stamped onto tracked-prompt rows for a shipped play
 *  (frozen-cohort baseline-run window). All null when the prompt wasn't in the
 *  ship cohort or the play hasn't shipped — render blank, never a fabricated
 *  before. */
export interface TrackedPromptBaselineFields {
  workspace_prompt_id?: string | null;
  baseline_outcome?: string | null;
  baseline_coverage?: string | null;
  baseline_score?: number | null;
}

/** The `/prompts` payload's at-ship baseline block for a shipped/measured play:
 *  the frozen min-seq cohort over `Window.for_run(baseline_run_id)` — the SAME
 *  universe measurement uses. `cohort_n === 0` = the cohort froze EMPTY at ship
 *  (structural: a per-play comparison will never be possible — say so). Null =
 *  not shipped, or the baseline is unresolvable (no frozen row / no baseline run). */
export interface PlaybookTrackingBaseline {
  run_id: string;
  run_date: string | null;
  cohort_n: number;
  win_rate: number | null;
  visibility_rate: number | null;
}

/** The `GET /workspaces/{ws}/playbooks/roadmap` block — a SIBLING of the bare
 *  list response (rows carry their own `phase`). `estimated` drives the DR13
 *  "· estimated" qualifier. */
export interface PlaybookRoadmapResponse {
  stage: string | null;
  stage_inputs_missing: boolean;
  estimated: boolean;
  /** F5: in-flight PLAYBOOK_GENERATE jobs (pending/processing) for the
   *  workspace. While > 0 a regeneration is still drafting briefs async — the
   *  list suppresses affirm lines, shows "N plays generating…", and polls. */
  generating_count: number;
  /** Missing table-stakes pages still being researched or authored. */
  discovery_generating_count: number;
  /** Server-owned batch state spanning synchronous planning and async authoring. */
  generation_in_progress: boolean;
  /** Typed terminal refusals from the latest planner batch. */
  generation_refusals: Array<{
    job_id: string;
    title: string;
    reason_code: string;
    detail: string;
  }>;
}

/** DR13 stage chip label — engine stage key → human word. */
export const RAMP_STAGE_LABELS: Record<string, string> = {
  sparse: 'Sparse',
  building: 'Building',
  established: 'Established',
};

/** Wire shape is `{question, answer}` (backend `GeoFaqPair`); some legacy rows
 *  carried `{q, a}` — normalize via `faqPairs()` before rendering. */
export interface PlaybookGeoFaqPair {
  question?: string;
  answer?: string;
  q?: string;
  a?: string;
}

export interface PlaybookGeoBrief {
  title_tag: string;
  slug: string;
  meta_description: string;
  h1: string;
  outline: string[];
  faq: PlaybookGeoFaqPair[];
  /** Written-out 40–60 word page-opening answer (additive — older rows absent). */
  answer_capsule?: string;
  /** 2–3 standalone quotable sentences (additive — older rows absent). */
  quotable_lines?: string[];
}

/** One lane-tuned GEO/AEO tactic chosen for the issue types a playbook covers. */
export interface PlaybookTactic {
  key: string;
  name: string;
  instruction: string;
  why: string;
}

/** Structured execution plan attached to a playbook (additive — older rows
 *  carry null/absent). */
/** One GEO tactic scored against the audited page (pass/partial/fail + finding). */
export interface PlaybookAuditFinding {
  key: string;
  status: 'pass' | 'partial' | 'fail';
  finding: string;
}

/** A buyer-vocabulary theme the audited page is missing. */
export interface PlaybookAuditVocabGap {
  theme: string;
  weight?: number;
  seen_in?: string;
}

/** Citation performance of the audited page vs the best page on the covered topics. */
export interface PlaybookAuditCitationContext {
  page_citations: number;
  topic_eval_count: number;
  best_page?: { url: string; owner?: string; citations: number } | null;
  best_external_page?: { url: string; owner?: string; citations: number } | null;
  /** Ranked runner-up external pages behind the selected page-to-beat (persisted
   *  at audit time; audits from before this field simply omit it — never guessed). */
  top_external_pages?: Array<{ url: string; owner?: string | null; citations?: number | null }> | null;
  /** Honesty flag for a thinly-evidenced page-to-beat pick (single-citation
   *  window / no topical-equivalence signal). `'low'` renders a quiet
   *  "low-confidence pick" label so the selection is never asserted as definitive
   *  (server: `_stamp_citation_window`). Additive — older audits omit it. */
  selection_confidence?: 'low' | 'high' | (string & {}) | null;
  /** The locked eval-window provenance the pick was scored over
   *  (`{basis, n_prompts, n_evals}`). MCP renders the selection rationale from it;
   *  the console conveys the same via the citations/answers count. Additive. */
  window_basis?: { basis?: string; n_prompts?: number; n_evals?: number } | null;
}

/** The page audit behind a revise playbook ("what's wrong with your page today") —
 *  or, when `benchmark` is true, the competitor page a create playbook must beat. */
export interface PlaybookAudit {
  target_url: string;
  audited_content_hash?: string;
  text_source?: 'corpus' | 'live_fetch' | 'metadata_only';
  /** 0–100 unified structural GEO score for the OWNED target page (same number
   *  /content shows) — looked up at generation time, never recomputed here.
   *  Absent for a `benchmark` (unowned rival) audit: there is no unified score
   *  for a page KnitKnot doesn't own (B5). */
  page_score?: number | null;
  tactic_findings: PlaybookAuditFinding[];
  vocabulary_gaps: PlaybookAuditVocabGap[];
  citation_context?: PlaybookAuditCitationContext | null;
  /** True when this audit scores the page to BEAT (create verb), not the customer's own. */
  benchmark?: boolean;
  /** Opening text (~1.5k chars) of the audited page, captured at audit time —
   *  lets the writer quote and out-specify it without a second manual fetch. */
  content_excerpt?: string | null;
  /** F4: ownership of the SELECTED benchmark page, resolved server-side from the
   *  canonical OWNER_JOIN — `us` | `competitor` | `third_party`. When `us` the
   *  top-cited page is one the customer already owns; the detail page frames it
   *  as "your page to build on" instead of "the page to beat". Trust this flag;
   *  do NOT re-derive ownership from the URL host. */
  benchmark_owner?: 'us' | 'competitor' | 'third_party' | string | null;
  /** Bins P1 (gap #2): the transparency receipt for a revise-shaped audit —
   *  whether the LLM-chosen target is among the top co-cited owned pages, plus
   *  those pages with their co-occurrence counts. A receipt the UI renders, never
   *  a silent target override. Absent on create/benchmark audits + older plays. */
  co_citation_note?: PlaybookCoCitationNote | null;
  /** Bins P1 (gap #5's silent path): true when the target page has NO stored
   *  corpus text, so we could not verify its structure. Rendered honestly as
   *  "page unreadable — not verified", never alarmingly. */
  page_content_unavailable?: boolean | null;
}

/** One owned page co-cited with external/rival sources in the play's damaging
 *  evaluations (bins P1) — the strongest reinforce candidate, ranked by how often
 *  it shows up next to the damaging sources. Pure canonical read ($0). */
export interface PlaybookCoCitedOwnedPage {
  url: string;
  url_hash?: string | null;
  /** Distinct answers in the play's window that cite this owned page. */
  n_evals_citing: number;
  /** How many times it was co-cited with a non-owned page in those answers. */
  n_cocited_with_external: number;
  /** Up to 3 external hosts it most-often appears beside. */
  top_cocited_domains: string[];
}

/** The co-citation transparency receipt stamped on a revise-shaped audit (bins
 *  P1, gap #2). */
export interface PlaybookCoCitationNote {
  chosen_target_in_top_cocited: boolean;
  top_cocited_owned: PlaybookCoCitedOwnedPage[];
}

/** D2.5 plan-time "why this page" diagnosis for a revise-verb item that targets
 *  an existence-matched owned page — cited-here vs cited-elsewhere on THIS
 *  playbook's topic (additive; older/thin plans carry it absent). Rendered in
 *  the Evidence tab (design doc F14). */
export interface PlaybookOwnedPageDiagnosis {
  url: string;
  /** Existence-match cosine similarity that surfaced this page, when known. */
  similarity?: number | null;
  match_tier?: 'soft' | 'hard' | string | null;
  cited_here: number;
  cited_elsewhere: number;
  n_owned_pages_on_topic: number;
  topic: string;
}

/** Existence-match evidence for the plan item's target — the match score +
 *  the planner's create-vs-revise decision (additive). */
export interface PlaybookMatchEvidence {
  url: string;
  similarity?: number | null;
  decision?: string | null;
}

/** One verbatim, dated quote pulled from the customer's own fact library —
 *  proven, attributable copy for the brief's Copy-blocks kicker (additive,
 *  max 2, ``claim_text`` already truncated to ≤200 chars server-side). */
export interface PlaybookFactQuote {
  claim_text: string;
  url: string;
  source_title?: string | null;
  published_at?: string | null;
}

/** E1 — one answer-first outline section of a `ContentBrief`: an H2 heading +
 *  a one-line intent (docs/designs/GEO_CONTENT_INTELLIGENCE_2.md §E1). */
export interface ContentBriefSection {
  heading: string;
  intent: string;
}

/** E1 — one quotable statistic/claim to place on the page, with its
 *  provenance. Only provenance-carrying facts ever become a `ContentBriefStat`
 *  (the P4 guarantee) — `source_url` is always present when the backend emits
 *  a stat, but the wire type stays nullable defensively. */
export interface ContentBriefStat {
  statement: string;
  source_url: string | null;
  /** 'company_fact' | 'owned_page' */
  provenance: string;
  published_at?: string | null;
  confidence?: string | null;
  /** Read-time canonical-ownership label on the grounding URL (server-stamped,
   *  never derived client-side): 'owned' = the customer's own claim,
   *  'third_party' = independent proof, 'competitor' = a rival's page. */
  independence?: 'owned' | 'third_party' | 'competitor' | (string & {}) | null;
}

/** One GEO structural fix in a content brief's "GEO fixes / checklist" section
 *  (backend `knitknot/core/playbooks/content_brief.py::StructuralFix`). For a
 *  REVISE target: a failing/partial tactic from the page audit (`status` +
 *  the audit `finding`). For a CREATE playbook: an archetype-appropriate
 *  checklist item (`status`/`finding` absent). */
export interface StructuralFix {
  key: string;
  name: string;
  instruction: string;
  why: string;
  status?: 'fail' | 'partial' | null;
  finding?: string | null;
}

export type ContentBriefDepth = 'concise' | 'standard' | 'comprehensive';

export const CONTENT_BRIEF_DEPTH_LABELS: Record<ContentBriefDepth, string> = {
  concise: 'Concise',
  standard: 'Standard',
  comprehensive: 'Comprehensive',
};

export const CONTENT_BRIEF_PROVENANCE_LABELS: Record<string, string> = {
  company_fact: 'Company fact',
  owned_page: 'Owned page',
  // Ledger facts grounded by an independent URL — reads as the independence
  // label so the meta line never shows "third_party · Independent" (one label).
  third_party: 'Independent',
};

/** P0-5 — one measured feature-matrix row of a comparison kit. All display
 *  strings ride in the payload, backend-authored (the em-dash guard covers
 *  them); the frontend only maps `posture` to a label. */
export interface ComparisonKitMatrixRow {
  dimension: string;
  wins: number;
  losses: number;
  ties: number;
  /** wins + losses + ties — the row's sample size, always shown. */
  n: number;
  /** Derived only from the counts, never prose. A below-floor row is always
   *  `insufficient` and renders its `below_floor_note` INSTEAD of a posture
   *  label (the two signals never co-render). */
  posture: 'you_lead' | 'rival_leads' | 'contested' | 'insufficient' | (string & {});
  below_floor: boolean;
  /** Backend-authored muted annotation for a below-floor row. */
  below_floor_note?: string | null;
  subject_feature_id?: string | null;
  member_subject_feature_ids?: string[] | null;
}

/** Posture display strings (frontend-rendered from the enum — §6). Em-dash
 *  free (round-8 policy on frontend-authored strings). `insufficient` is
 *  deliberately absent: a below-floor row renders its backend-authored
 *  `below_floor_note` instead of a posture label. */
export const COMPARISON_KIT_POSTURE_LABELS: Record<string, string> = {
  you_lead: 'You lead',
  rival_leads: 'Rival leads',
  contested: 'Contested',
};

/** P0-5 — the comparison kit's measured feature matrix. `basis_line` is
 *  generation-dated (the kit freezes in JSONB while the live window moves). */
export interface ComparisonKitMatrix {
  competitor_id: string;
  competitor_name: string;
  basis_line: string;
  rows: ComparisonKitMatrixRow[];
}

/** P0-5 — the comparison-page generation kit at
 *  `plan.content_brief.comparison_kit`
 *  (`knitknot/core/playbooks/comparison_kit.py::build_comparison_kit`).
 *  Additive — absent on non-comparison plays and rows that predate P0-5.
 *  When present, the kit card OWNS JSON-LD rendering and the standalone
 *  `schema_block` section is suppressed for that play (§4.4 dedup rule). */
export interface ComparisonKit {
  kit_version: number;
  registry_as_of: string;
  /** Generation date (ISO) — drives every dated display string. */
  as_of: string;
  page_type: 'vs' | 'alternatives' | 'roundup' | (string & {});
  page_type_label: string;
  selection_rationale: string;
  competitor_id?: string | null;
  competitor_name?: string | null;
  competitor_domain?: string | null;
  title_formulas: string[];
  h1_formulas: string[];
  cta_guidance: string[];
  social_proof_guidance: string;
  matrix?: ComparisonKitMatrix | null;
  /** Backend-authored honest note when the kit ships without a matrix. */
  no_matrix_note?: string | null;
  /** Paste-ready JSON-LD blocks — rendered through `SchemaCodeBlock`. */
  json_ld: Record<string, unknown>[];
  /** "surface" when the target platform cannot carry JSON-LD (render sites
   *  must NOT fall back to the standalone schema_block); null when json_ld is
   *  populated or was dropped for content reasons (fallback is honest). */
  json_ld_omitted_reason?: 'surface' | null;
  /** Capability-gated, phrased with the target surface's `edit_how`. */
  linking_instructions: string[];
  /** Mandatory content: disclosure + as-of dating + balanced presentation. */
  fairness_boilerplate: string[];
}

/** E1 — the near-ready draft attached to a playbook's `plan.content_brief`
 *  (`knitknot/core/playbooks/content_brief.py::ContentBrief`). Additive —
 *  absent on playbooks generated before E1 shipped. */
export interface ContentBrief {
  title: string;
  depth: ContentBriefDepth;
  sections: ContentBriefSection[];
  stats: ContentBriefStat[];
  vocabulary: string[];
  schema_block: Record<string, unknown>;
  /** Honest "add your own stat here" prompts — populated only when the
   *  harvest bank had no verified proof point. */
  placeholders: string[];
  /** True when no verified stat backed the brief — render `placeholders`
   *  instead of `stats`, never a fabricated number. */
  is_skeleton: boolean;
  /** GEO advice on every page-touching playbook: the target page's failing
   *  structural tactics (revise) or the new page's archetype checklist
   *  (create). Empty when a revise target is near-perfect — render nothing. */
  structural_fixes: StructuralFix[];
  /** 'create' -> "GEO checklist for the new page"; anything else (an
   *  existing-page verb — promote/reinforce, legacy 'revise') -> "GEO fixes
   *  for this page". Null when `structural_fixes` is empty. */
  structural_fixes_kind: 'create' | 'promote' | 'reinforce' | 'revise' | (string & {}) | null; // legacy 'revise' wire value
  /** Server-stamped independence summary over `stats` — 'owned_only' means
   *  every proof point is self-cited (render the attribute-don't-cite note). */
  stats_independence?: 'owned_only' | 'mixed' | 'third_party' | (string & {}) | null;
  stats_independence_note?: string | null;
  /** P0-5 — the comparison-page generation kit (comparison plays only;
   *  additive). When present, the kit card owns JSON-LD rendering and the
   *  standalone `schema_block` section is suppressed. */
  comparison_kit?: ComparisonKit | null;
}

/**
 * §4.4 one-render-site dedup — THE predicate for suppressing the standalone
 * `schema_block` (PlanTab render + copy-all markdown + the MCP payload's
 * Python twin in `content_strategy.py`). The kit owns JSON-LD when it CARRIES
 * blocks, or when its blocks were omitted because the target surface cannot
 * carry JSON-LD (falling back would bypass that gate). A kit whose blocks
 * were dropped for content reasons does NOT own it — the standalone block is
 * still honest there, and suppressing it would ship no structured data at all.
 */
export function kitOwnsJsonLd(kit: ComparisonKit | null | undefined): boolean {
  if (!kit) return false;
  return kit.json_ld.length > 0 || kit.json_ld_omitted_reason === 'surface';
}

/** One measured per-feature W-L-T row vs the play's competitor(s) — the honest
 *  comparison table skeleton the brief's outline must honor (`plan.comparison_skeleton`).
 *  Only decided, above-floor features survive; every number is measured, never invented. */
export interface PlaybookComparisonRow {
  feature: string;
  wins: number;
  losses: number;
  ties: number;
  /** WHO the record is against — the competitor display names behind this row's
   *  W/L/T (server `_comparison_skeleton`). Additive: rows persisted before the
   *  field simply omit it, and the console then can't scope the table to the
   *  play's rivals (it renders a provenance caption instead). */
  competitor_names?: string[] | null;
}

/** One verbatim, dated correcting quote from the customer's own fact library
 *  (`plan.corrective_quotes`) — proof that rebuts what AI currently says. */
export interface PlaybookCorrectiveQuote {
  claim_text: string;
  url: string;
  source_title?: string | null;
  published_at?: string | null;
}

/** One demand-priced keyword target (`plan.keyword_targets`) — the buyer-terms
 *  fallback rendered when the play carries no audit/content_brief vocabulary
 *  (all 9 Intryc promote plays). */
export interface PlaybookKeywordTarget {
  keyword: string;
  search_volume: number;
  cpc: number | null;
  /** Bins P1 (gap #3): true when the term is already present on the target
   *  page's stored corpus. The term STAYS in the list (transparency — "you
   *  already use this"), but the brief's recommended vocabulary only recommends
   *  the absent ones. Shown as an "On page" ✓, never a to-do. */
  on_page?: boolean | null;
}

/** The advisory playbook-critic verdict (`plan.quality`) — an honesty signal on
 *  the brief's own copy quality (pass flag + a one-line summary). Never a gate. */
export interface PlaybookCritic {
  pass?: boolean | null;
  summary?: string | null;
}

/** The kinds of commonality receipt a bin can carry (data-bins §2a). Each names
 *  a data-derived reason a set of issues share this play. */
export type PlaybookCommonalityKind =
  'topic' | 'feature' | 'competitor' | 'theme' | 'source_domain' | 'eval_overlap' | 'archetype_cell';

/** ONE commonality receipt — the structured "why these issues belong together"
 *  (data-bins §2a). `value` carries a human label for `topic`/`source_domain`/
 *  `archetype_cell`; for `competitor`/`feature`/`theme` it carries the FK id (no
 *  name is available client-side — resolve competitors via the linked issues).
 *  Counts are per-kind: `n_claims` (source_domain), `n_shared_evals` (eval_overlap). */
export interface PlaybookCommonality {
  kind: PlaybookCommonalityKind | (string & {});
  /** Human label (topic/source_domain/archetype_cell) or FK id (competitor/feature/theme). */
  value?: string | null;
  /** The issues this receipt binds together. */
  issue_ids: string[];
  /** source_domain: claims attributing to the domain across the bin's members. */
  n_claims?: number | null;
  /** eval_overlap: answers that literally exhibit both issues. */
  n_shared_evals?: number | null;
  topic_id?: string | null;
  competitor_id?: string | null;
  /** vocab-overlap receipts carry the shared terms instead of a single value. */
  terms?: string[] | null;
}

export interface PlaybookPlan {
  /** Covered issues excluded from the refreshed brief because they went dormant
   *  with zero reach (D1 stale receipt) — links are KEPT for measurement identity;
   *  the Issues tab renders these dimmed with an honest 'no longer observed' state. */
  stale_covered_issue_ids?: string[];
  geo_brief?: PlaybookGeoBrief;
  rationale?: string;
  /** Data-bins §2a — the structured receipts that explain WHY this play's issues
   *  share one bin. ABSENT/null on legacy plays (predate binning); the UI hides
   *  the common-threads surfaces entirely rather than showing an empty state. */
  commonalities?: PlaybookCommonality[] | null;
  /** Measured per-feature W-L-T rows vs the competitor(s) — the honest
   *  comparison table (comparison plays only; additive). */
  comparison_skeleton?: PlaybookComparisonRow[] | null;
  /** Verbatim owned-source quotes that correct what AI currently says (additive). */
  corrective_quotes?: PlaybookCorrectiveQuote[] | null;
  /** Demand-priced keyword targets — the buyer-terms fallback (additive). */
  keyword_targets?: PlaybookKeywordTarget[] | null;
  /** Advisory playbook-critic verdict on the brief's own quality (additive).
   *  INTERNAL telemetry — read by MCP/staff surfaces, never rendered on the
   *  customer console (QA 2026-07-24). */
  quality?: PlaybookCritic | null;
  /** Earn plays: the deterministic earn target frozen at mint. Amplify plays
   *  carry the advocate receipts (`pos_per_answer` + `mean_pos_per_answer` +
   *  the `baseline` citing-answer count) the header sentence is computed from. */
  earn_target?: {
    url?: string | null;
    url_hash?: string | null;
    domain?: string | null;
    baseline?: number | null;
    pos_per_answer?: number | null;
    mean_pos_per_answer?: number | null;
  } | null;
  /** Earn kind discriminator ('listing' | 'correction' | 'amplify') — the ER2
   *  measurement identity, stamped at mint. */
  earn_kind?: 'listing' | 'correction' | 'amplify' | (string & {}) | null;
  /** Listing plays: the absence-gate verdict stamped at mint. 'existing' = the
   *  corpus PROVES a read page on the domain mentions the customer; 'none' =
   *  verified page-level absence; 'unverified' = no readable page (crawl-blocked)
   *  — the copy never claims page-level absence for it. */
  listing_state?: 'existing' | 'none' | 'unverified' | (string & {}) | null;
  /** Earn plays: the "This source today" receipt strip — the earn twin of the
   *  fix lane's `page_snapshot`. Only numbers the mint pass already held
   *  (outreach targets / advocate claim profile / leak components); everything
   *  is measured from AI ANSWERS citing the third-party target (`basis`), never
   *  asserted about the page itself. Absent on rows minted before 2026-07-24. */
  earn_target_snapshot?: PlaybookEarnTargetSnapshot | null;
  /** How the promote/reinforce split was decided (§2.4): `'measured'` = the
   *  citation-count query succeeded; `'assumed_uncited'` = the lookup failed /
   *  URL unresolvable (defaults to promote). An assumed basis SUPPRESSES the
   *  citation receipt in the UI — a data failure is never presented as strategy. */
  verb_basis?: 'measured' | 'assumed_uncited' | (string & {}) | null;
  /** Evidence-backed methodology for the play (additive — older rows absent). */
  tactics?: PlaybookTactic[] | null;
  /** Page audit for revise targets / create benchmarks (additive — older rows absent). */
  audit?: PlaybookAudit | null;
  /** "Why this page" diagnosis for revise plays (additive — older rows absent). */
  owned_page_diagnosis?: PlaybookOwnedPageDiagnosis | null;
  /** Existence-match evidence for the plan item's target (additive). */
  match_evidence?: PlaybookMatchEvidence | null;
  /** Fact-library quotes for the brief's Copy-blocks kicker (additive, max 2). */
  fact_quotes?: PlaybookFactQuote[] | null;
  /** True when no owned page states the corrected fact — this page must be
   *  the first to publish it (additive — older rows absent). */
  unpublishable_correction?: boolean | null;
  /** E1 — the auto-generated near-ready content brief (additive — absent on
   *  playbooks generated before E1 shipped). */
  content_brief?: ContentBrief | null;
  /** $/mo this playbook addresses — the paid-search value of the AI answers it would
   *  flip (Σ of the covered issues' evidence-prompt shares, deduped by prompt). Null when
   *  unpriced. Additive — absent on older rows. */
  covered_demand_monthly?: number | null;
  /** Non-null topic ids this playbook covers (sorted, deduped). Additive. */
  covered_topic_ids?: string[] | null;
  /** Per-prompt share breakdown — the distinct AI answers this play targets, each priced.
   *  Lets the portfolio header count each answer once across playbooks (max share per
   *  prompt). Additive — absent on rows generated before Phase D. */
  covered_prompt_shares?: { prompt_id: string; share: number | null }[] | null;
  /** LEGACY (pre-Phase-D rows only) — per-topic demand breakdown; the header falls back
   *  to it when covered_prompt_shares is absent. */
  covered_topics?: { topic_id: string; demand_value_monthly: number }[] | null;
  /** LEGACY (pre-Phase-D rows only) — summed $/mo of covered issues with NO topic. */
  covered_untopic_demand?: number | null;
  /** Fix plays (site_fix) — deterministic-verification identity + baselines. */
  fix_kind?: string | null;
  fix_target?: string | null;
  fix_check?: string | null;
  /** Fix structure play (page_geo_health) — the receipt-driven work order: one row
   *  per failing structural component with the concrete instruction AND the scoring
   *  rubric's own full-credit definition (fix-verb quality pass, 2026-07-24). */
  fix_requirements?: PlaybookFixRequirement[] | null;
  /** Fix page plays — the "This page today" receipt strip: only facts the crawl +
   *  benchmarks verifiably hold (absent fields were unknown, never guessed). */
  page_snapshot?: PlaybookPageSnapshot | null;
}

/** One work-order row of a structure-fix play (plan.fix_requirements). */
export interface PlaybookFixRequirement {
  key: string;
  label: string;
  instruction: string;
  /** The rubric's own definition of full credit for this component; null when unmapped. */
  full_credit?: string | null;
}

/** The "This source today" receipt strip on an earn play (plan.earn_target_snapshot).
 *  Third-party-target twin of `PlaybookPageSnapshot`: claim-profile + citation
 *  numbers only (the entity map is owned-only, so no `covers_*` here — we can't
 *  read arbitrary external pages and never pretend to). */
export interface PlaybookEarnTargetSnapshot {
  kind: 'listing' | 'correction' | 'amplify' | (string & {});
  /** Always 'answers': every number is measured from AI answers citing the target. */
  basis: string;
  domain?: string | null;
  url?: string | null;
  url_hash?: string | null;
  /** Answers citing the target in the mint-time window. */
  citing_answers?: number | null;
  /** Listing: total citation rows behind those answers. */
  citations?: number | null;
  /** Correction: distinct engines whose answers cite the page. */
  engines?: number | null;
  /** Correction: hard-tier claim counts from the leak's evidence. */
  inaccurate_claims?: number | null;
  fabricated_claims?: number | null;
  /** Amplify: the frozen claim-profile rates. */
  pos_per_answer?: number | null;
  neg_per_answer?: number | null;
  mean_pos_per_answer?: number | null;
  /** Listing: the true split over the citing answers. */
  we_win?: number | null;
  absent_or_loss?: number | null;
  listing_state?: 'existing' | 'none' | 'unverified' | (string & {}) | null;
  /** Present only when the target is a KNOWN self-serve platform (registry). */
  platform?: { name: string; noun: string; self_serve: boolean } | null;
}

/** The "This page today" receipt strip on a page-grain fix play (plan.page_snapshot). */
export interface PlaybookPageSnapshot {
  url: string;
  url_hash?: string | null;
  title?: string | null;
  /** Citation ROWS in the current window (the /sources "Cited N" convention). */
  citations?: number | null;
  /** Structural GEO score 0–100 (page_geo_health plays). */
  geo_score?: number | null;
  archetype?: string | null;
  /** Citation-effectiveness block fields (same read as /sources/{hash}). */
  effectiveness_score?: number | null;
  silent_share?: number | null;
  pull_through_rate?: number | null;
  vis_cites?: number | null;
  pos_claims_per_citing_answer?: number | null;
  neg_claims_per_citing_answer?: number | null;
  /** Entity-map coverage (what the page verifiably covers per the crawl). */
  covers_features?: string[] | null;
  covers_topics?: string[] | null;
  covers_competitors?: string[] | null;
  mentions_you?: boolean | null;
}

/** Response of POST /playbooks/regenerate-plan — carries planner reasons for
 *  declared issues it intentionally left uncovered (additive). */
export interface RegeneratePlanResponse {
  uncovered_declared?: { issue_id: string; reason: string }[] | null;
  /** Open issues held back because a shipped playbook is still awaiting
   *  measurement from an already-shipped fix (additive). */
  awaiting_measurement_excluded?: number | null;
}

export interface PlaybookListItem {
  id: string;
  workspace_id: string;
  product_id: string | null;
  /** Discriminates the artifact union: V1 is Legacy; V2 has an execution plan. */
  playbook_schema_version?: 1 | 2;
  /** V2 readiness: research-only rows remain retained opportunities, not ready-to-ship work. */
  delivery_state?: 'patch_ready' | 'editorial_ready' | 'content_brief' | 'research_only' | null;
  family: PlaybookFamily;
  title: string;
  hypothesis: string;
  action_label: string | null;
  action_rationale: string | null;
  /** Artifact shape. Tolerant of legacy strings during the readers-first deploy
   *  window (rows are retyped by the post-deploy sweep) — render via
   *  `playbookTypeLabel`, never a bare map lookup. */
  playbook_type: PlaybookType | (string & {});
  status: PlaybookStatus;
  priority: PlaybookPriority;
  /** 0–100 ROI ranking score; rows are ordered most-important-first by it.
   *  Nullable for rows that predate the column. */
  importance_score: number | null;
  priority_components: Record<string, number>;
  shipped_url: string | null;
  /** One canonical target across V1/V2: planned before ship, attested live URL
   * after ship. Null when the play targets a domain rather than a page. */
  page_url?: string | null;
  /** One-shot liveness/content check of shipped_url; null = never checked. */
  shipped_url_check: ShippedUrlCheck | null;
  /** Set only when the detector shipped this play from a published page. */
  ship_detection?: ShipDetection | null;
  shipped_at: string | null;
  dismissed_reason: string | null;
  dismissed_note: string | null;
  /** Notion-synced assignee — read-only in the console. */
  assigned_to?: { name?: string | null; email?: string | null; notion_user_id?: string | null } | null;
  /** The play family (create|promote|reinforce|fix|earn). Null/absent on rows
   *  that predate the column; legacy `revise`/`defend` strings may transiently
   *  appear during the deploy window — always render via `playbookVerbLabel`. */
  verb?: PlaybookVerbWire | null;
  /** The verb registry payload for this row — render labels/blurbs/recipes from
   *  it when present; local maps are deploy-window fallbacks only. */
  verb_info?: PlaybookVerbInfo;
  /** The origin tag `'defend'` when the erosion trigger minted this play (Defensive tag). */
  origin?: PlaybookOrigin | null;
  /** Empirical age-at-citation window for this verb's cohort (D5). */
  measured_window?: PlaybookMeasuredWindow | null;
  /** Content-plan list columns (issue_playbook_links coverage + covered demand). */
  linked_issue_count?: number | null;
  linked_issue_ids?: string[] | null;
  /** issue_id → demand $/mo — union-deduped header math (A5) */
  linked_issue_demands?: Record<string, number> | null;
  /** Σ of the linked issues' current reach (deleted issues excluded) — the
   *  list-grain twin of the detail header's Covers-rows sum. Preferred over
   *  the frozen n_losing_evals for the unpriced-$ "N answers" fallback; null
   *  when the play has no linked live-issue reach. */
  linked_issue_reach?: number | null;
  /** Cited column — SHIPPED plays only (backend `_stamp_page_citations`).
   *  Distinct answers in the live window that cite this play's shipped page.
   *  A real `0` is the signal "shipped and AI still ignores it"; `null` means
   *  not measurable here (not shipped, or no page) and the cell renders blank.
   *  NOTE the name: `PlaybookImpact.cited_answers` is a different concept (the
   *  per-answer LIST on the detail payload) — this is the scalar count. */
  cited_answer_count?: number | null;
  /** Window size the count is out of — the hover's denominator. */
  cited_answer_total?: number | null;
  /** The same count at the play's frozen at-ship baseline run. `null` when the
   *  baseline is unknown, and the UI then says nothing about "before" rather
   *  than implying zero. This is what keeps a `reinforce` play honest: its page
   *  was ALREADY cited, so the current count alone is not the play's impact. */
  cited_answer_count_at_ship?: number | null;
  /** Per-benchmark citing-answer counts for this play's page, OLDEST first, over
   *  the workspace's last 12 completed full runs. Drawn as the AI pickup
   *  sparkline: one window-scoped number reads as an all-time total and is not
   *  one, so the SHAPE is what distinguishes "picked up all along" from "picked
   *  up once". Empty when the play is unshipped or the series read failed. */
  citation_series?: PlaybookCitationSeriesPoint[];
  /** Days from ship to the first post-ship benchmark that cited the page. `null`
   *  — never 0 — when it has not been cited since ship or has no ship date;
   *  "not yet" and "same day" are different facts. */
  days_to_first_citation?: number | null;
  demand_value_monthly?: number | null;
  /** Structured execution plan (GEO brief + rationale). Null/absent on rows
   *  that predate the column. */
  plan?: PlaybookPlan | null;
  created_at: string | null;
  updated_at: string | null;
  n_losing_evals: number;
  n_engines: number;
  search_volume_sum: number;
  avg_cpc: number | null;
  max_importance: number;
  high_importance_count: number;
  run_ids: string[];
}

/** Convenience alias — the list-grain row IS the playbook shape most surfaces
 *  consume (TrendHero's ship markers, dashboard widgets). */
export type Playbook = PlaybookListItem;

export type MisrepTier = 'critical' | 'high' | 'moderate' | 'low';

/** How a tier of evidence was scoped. `company` = drawn from ALL of the
 *  company's answers (a workspace-wide fallback), not the playbook's specific
 *  topic — so the section must be labelled honestly as company-wide rather than
 *  implied to be topic-specific. Emitted by the backend topic-scope work; read
 *  additively (absent → treat as the playbook's own topic scope). `workspace`
 *  is an alias the backend may emit for the same company-wide meaning. */
export type PlaybookEvidenceScope = 'topic' | 'company' | 'workspace';

/** Section 6 — a false thing AI confidently tells buyers, ranked by severity. */
export interface PlaybookMisrepresentation {
  /** What AI claims (the false statement). */
  claim: string;
  /** The truth / correction. */
  truth?: string | null;
  /** 0–100 severity score. */
  severity?: number | null;
  /** Coarse tier (critical/high/moderate/low). */
  tier?: MisrepTier | string | null;
  /** Engines that stated the claim. */
  engines?: string[] | null;
  /** Distinct engines that stated the claim (true spread, merge-aware). */
  engine_count?: number | null;
  /** How many prompts surfaced the claim. */
  prompt_count?: number | null;
  /** Canonical factual status — 'fabricated' (invented) reads worse than
   *  'inaccurate' (distorted). Drives the honest per-item label. */
  claim_status?: 'fabricated' | 'inaccurate' | null;
  /** AI's hedging on the false claim — 'certain' reads worst (stated as fact). */
  ai_confidence?: 'certain' | 'tentative' | 'uncertain' | null;
  /** A source URL the claim leans on, when known. */
  source_url?: string | null;
  /** The AI's EXACT words for the claim, when captured — quoted directly
   *  instead of the paraphrased `claim`. */
  verbatim_quote?: string | null;
  /** Topic vs company-wide scope (additive — see PlaybookEvidenceScope). */
  scope?: PlaybookEvidenceScope | null;
  /** True when this row is a workspace-wide fallback (topic was too thin). */
  is_fallback?: boolean | null;
}

/** W5-A ripple impact — the observational "did my work pay off" payload for a
 *  DONE playbook. Keyed on whether the shipped/created/fixed page is cited as a
 *  source in downstream issues' evidence in subsequent benchmark runs (one
 *  playbook can ripple into many issues, assigned AND unassigned). Additive —
 *  absent on payloads generated before W5-A shipped, or on non-done rows. */
export type PlaybookImpactState = 'awaiting_next_run' | 'measuring';

/** Honest citation-tier ladder for a shipped play's page: 'shipped' = a URL was
 *  shipped (nothing more proven) · 'content_verified' = a real before→after content
 *  change was captured · 'cited' = the canonical shipped URL was cited post-ship.
 *  Never 'verified' — that word is reserved for content_attribution (hash-verified). */
export type PlaybookCitationTier = 'shipped' | 'content_verified' | 'cited';

/** Honest strength-of-evidence that a play's revision actually landed on the page:
 *  'verified' = we captured a real before→after content change · 'attested' = the
 *  customer marked the play shipped with a URL (real attestation) but we never
 *  captured a diff · null/absent = cited-only. Never overclaimed. */
export type ContentAttributionTier = 'verified' | 'attested';

/** Cited-answer count for the playbook's page in one benchmark run. The series
 *  spans BOTH sides of the ship — see `pre_ship`. */
export interface PlaybookCitationRun {
  run_id: string;
  run_date: string | null;
  cited_eval_count: number;
  /** Denominator for the cited rate (cited / total): the eval-window size this run
   *  was measured over. */
  total_eval_count: number;
  tier: PlaybookCitationTier;
  /** This benchmark ran at or before the ship. Its count is REAL (a reinforce
   *  play's page is often already cited), but it never earns the play tier
   *  credit — which is why a pre-ship point with citations still reads
   *  'shipped'/'content_verified'. Without this side, "AI was not citing this
   *  page, and now it is" cannot be shown at all. */
  pre_ship?: boolean;
}

export interface PlaybookFormatCitationRun {
  run_id: string;
  run_date: string | null;
  html_cited_eval_count: number;
  markdown_cited_eval_count: number;
  both_cited_eval_count: number;
  combined_cited_eval_count: number;
  total_eval_count: number;
  pre_ship: boolean;
}

/** How one answer treated the company at one point in time. Both `outcome` and
 *  `coverage` ride along — a competitive prompt reads its H2H verdict, a
 *  visibility prompt reads coverage — so the renderer picks per row. */
export interface PlaybookAnswerPosture {
  outcome: string | null;
  coverage: string | null;
  score: number | null;
}

/** One answer that CITES the shipped page, at the baseline run vs today.
 *
 *  Keyed on the citation, not on cohort membership: a page AI decides to cite
 *  changes that answer whether or not the play ever targeted that prompt. This
 *  is the lane that shows reach beyond the target. */
export interface PlaybookCitedAnswer {
  evaluation_id: string;
  workspace_prompt_id: string | null;
  prompt_text: string | null;
  prompt_category: string | null;
  engine: string | null;
  /** true = a prompt the play targeted · false = reach beyond it · null = the
   *  play froze no cohort, so there is nothing to be in or out of. */
  in_cohort: boolean | null;
  /** null when the prompt was not benchmarked at the baseline run — it has no
   *  "before", and the cell renders blank rather than a fabricated zero. */
  baseline: PlaybookAnswerPosture | null;
  current: PlaybookAnswerPosture;
  score_delta: number | null;
}

/** One benchmark run on the "problem answers across the targeted issues" trend —
 *  the sum of the linked issues' reach at that run. The Impact-tab line. */
export interface PlaybookProblemPoint {
  run_date: string | null;
  problem_answers: number;
}

/** assigned = the playbook was created to cover this issue (∈ plan.covered_issue_ids);
 *  ripple = the shipped page got cited in this issue's answers even though the
 *  playbook wasn't created for it. */
export type PlaybookIssueRelationship = 'assigned' | 'ripple';

/** One downstream issue the playbook's page affected — assigned or ripple. */
export interface PlaybookIssueAffected {
  issue_id: string;
  title: string | null;
  issue_type: string;
  relationship: PlaybookIssueRelationship;
  /** Answers within this issue's evidence that cite the playbook's page (post-ship). */
  cited_answer_count: number;
  /** The issue's reach when the play shipped — the honest before number. */
  reach_at_ship: number | null;
  /** The issue's reach at the latest run. */
  reach_now: number | null;
  unit_label: string;
  impact_mode: 'mass' | 'resolution_only';
  /** `not_measurable` = claims-basis reach cannot be honestly windowed here
   *  (reach fields are null, never a fabricated 0) — see issue detail instead. */
  verdict: 'resolved' | 'improving' | 'no_change' | 'regressed' | 'pending' | 'not_measurable';
  confirming: boolean;
  has_baseline: boolean;
  attributed: boolean;
  attribution_status:
    | 'supported'
    | 'awaiting_measurement'
    | 'observed_without_citation'
    | 'citation_without_verified_change'
    | 'metric_not_directional'
    | 'withheld_data_quality';
  /** Receipt-gated delivered value. */
  value_delivered_monthly: number | null;
  /** Issue context only; never presented as delivered. */
  observed_value_monthly: number | null;
  last_event_at: string | null;
  /** Reach at each of the last few benchmarks, oldest→newest — the inline
   *  sparkline, the SAME column the /issues list draws. It replaced a movement
   *  WORD that was blank on most rows (a resolution-only lane can never state a
   *  direction): a shape is readable where a word is not, and the issue's own
   *  page has the detail for anyone who wants it. */
  reach_series?: number[];
}

/** W5-A — assembled per-playbook ripple impact (chart + issues-affected table). */
/** Attributed claims ABOUT US by polarity. `misrepresentation` is the verdict
 *  subset (inaccurate|fabricated), NOT a fourth polarity — it overlaps the three
 *  above and must never be summed with them into a total. */
export interface PlaybookClaimTally {
  positive: number;
  negative: number;
  neutral: number;
  misrepresentation: number;
}

/** What the shipped page BACKS in AI answers, now vs at the baseline run.
 *
 *  Counts, deliberately never rates: measured on real data a purpose-built page
 *  runs 97.4% positive against a site-wide 98.1%, so a rate would invite a
 *  quality comparison the data does not support. The honest claim is that the
 *  claims are NET NEW. `comparable: false` means there was no baseline window to
 *  read — render the current side alone rather than a "0 → 20" against nothing. */
export interface PlaybookClaimReceipt {
  coverage: 'attributed_claims_only';
  current: PlaybookClaimTally;
  current_claim_count: number;
  current_answer_count: number;
  baseline: PlaybookClaimTally | null;
  baseline_claim_count: number | null;
  comparable: boolean;
}

/** One register's answer transitions. `no_baseline` is load-bearing: an answer
 *  whose prompt was not benchmarked at the baseline run has no before, so
 *  counting its win as *newly* won would manufacture a victory. */
export interface PlaybookFlipCounts {
  gained: number;
  lost: number;
  unchanged: number;
  no_baseline: number;
  answers: number;
  /** Answers with a reading at BOTH benchmarks — the honest denominator. */
  measured: number;
  /** `(gained - lost) / measured`. THE field the UI compares on: the untouched
   *  group is always far larger, so raw nets across the two are not comparable
   *  ("+5 treated vs +6 untouched" is in fact 2.5x the rate). Null when nothing
   *  was measured — never a fabricated 0. */
  net_rate: number | null;
}

/** Before→after over the answers that CITE the page, beside the same ledger over
 *  the answers no shipped play touched.
 *
 *  `untreated` is not optional in the UI: "11 gained" means nothing until the
 *  reader knows how the untouched answers moved over the same weeks. Split by
 *  register because a visibility prompt has no head-to-head verdict and a
 *  competitive prompt has no coverage flip. */
export interface PlaybookFlipLedger {
  register: 'visibility' | 'competitive';
  treated: PlaybookFlipCounts;
  untreated: PlaybookFlipCounts | null;
  excluded_prompt_count: number;
  net_flips: number;
}

export interface PlaybookCitationMixPoint {
  owned_citations: number;
  playbook_page_citations: number;
  other_owned_citations: number;
  playbook_share_of_owned: number | null;
}

/** Share of the workspace's OWNED citations landing on playbook pages, one run
 *  against another. CO-MOVEMENT, NOT ATTRIBUTION — `caveat` is required and must
 *  always be rendered with the number. */
export interface PlaybookCitationMix {
  relationship: 'correlational';
  caveat: string;
  baseline: PlaybookCitationMixPoint | null;
  latest: PlaybookCitationMixPoint | null;
  playbook_share_change_pp: number | null;
}

/** The workspace's own ship→first-citation lag for this play's verb, EXCLUDING
 *  this play. Render only above a real `n` — a median of two is not a population. */
export interface PlaybookPeerLag {
  median_days: number | null;
  p25_days: number | null;
  p75_days: number | null;
  n: number;
}

export interface PlaybookImpact {
  state: PlaybookImpactState;
  /** The tracked page + its attribution tier (null when the completed play did
   *  not record an exact shipped_url). */
  url?: string | null;
  tier?: PlaybookCitationTier | null;
  /** Honest strength-of-evidence that the revision landed:
   *  'verified' = captured before→after content change · 'attested' = customer
   *  marked shipped with a URL, no captured diff · null = cited-only. Flag, not gate. */
  content_attribution?: ContentAttributionTier | null;
  /** When the page change this play produced was first CAPTURED
   *  (content_versions.fetched_at, ISO) — "Page change captured {date}". */
  content_captured_at?: string | null;
  citations_over_runs: PlaybookCitationRun[];
  format_citations_over_runs?: PlaybookFormatCitationRun[];
  problem_answers_over_runs: PlaybookProblemPoint[];
  problem_series_comparable?: boolean;
  issues_affected: PlaybookIssueAffected[];
  /** How AI frames the company in the latest run's answers that CITE the shipped
   *  page — the attribution proof (where cited = positive/neutral, never negative). */
  cited_sentiment?: { positive: number; neutral: number; negative: number } | null;
  /** Per-answer before→after for the answers citing the page in the latest
   *  post-ship run, biggest movers first. Empty until something cites it. */
  cited_answers?: PlaybookCitedAnswer[];
  /** ── The EARNED tier: deterministic receipts, never an estimate ───────────
   *  Counts and dates off `source_citations` / `claims`. Rendered in the header
   *  and the Impact tab's flip strip, and kept strictly apart from the CONTROLLED
   *  `PlaybookDetail.measured_impact` — the only attributable delta on the page. */
  claim_receipt?: PlaybookClaimReceipt | null;
  flip_ledger?: PlaybookFlipLedger[];
  citation_mix?: PlaybookCitationMix | null;
  days_to_first_citation?: number | null;
  peer_lag?: PlaybookPeerLag | null;
}

/** Closed-loop before/after for a SHIPPED play — its register metric at the
 *  baseline-run window vs now, plus how far toward the projected ceiling. */
/** TIER 1 — the UNCONTROLLED before/after: the play's own prompts at its baseline
 *  run vs their live pointers. No holdout, no CUPED, no sample gate, so it states
 *  what MOVED and never what the play moved.
 *
 *  These fields sat flat on `PlaybookMeasuredImpact` and were merged OVER the
 *  controlled ones, which is how the header came to render "Visibility at ship 0%
 *  → now 15% ↑15pp" in one tile and "Attributable to this page −1.8pp" in the
 *  next. Both were right; presenting them as one row was not. They are namespaced
 *  now so a renderer has to choose a tier, and the rule that falls out of it:
 *  never put a number from here beside a `cuped_*` effect without saying so. */
export interface PlaybookUncontrolledImpact {
  metric: {
    baseline: number | null;
    current: number | null;
    baseline_losses?: number;
    current_losses?: number;
    baseline_decided?: number;
    current_decided?: number;
    baseline_absent?: number;
    current_absent?: number;
    baseline_present?: number;
    current_present?: number;
  };
  delta_pp: number | null;
  projection_ceiling: number | null;
  realized_fraction: number | null;
  total_answers_now: number | null;
  improved: boolean;
  improved_basis?: string;
}

export interface PlaybookMeasuredImpact {
  /** Which metric this play is measured in. A property of the PLAY, so it is
   *  hoisted above both tiers — the controlled side needs it to pick between
   *  `cuped_visibility_effect` and `cuped_win_rate_effect`. */
  register: 'visibility' | 'competitive';
  /** TIER 1. Absent when there is no newer run than the baseline. */
  uncontrolled?: PlaybookUncontrolledImpact | null;
  /** TIER 2 — affected-vs-holdout attribution (fraction deltas; null until measured). */
  affected_win_rate_delta?: number | null;
  holdout_win_rate_delta?: number | null;
  affected_visibility_delta?: number | null;
  holdout_visibility_delta?: number | null;
  affected_prompt_count?: number | null;
  holdout_prompt_count?: number | null;
  /** Sample-size honesty gate: when `sufficient` is false the affected/holdout
   *  deltas above are withheld (null) — render "not enough affected prompts to
   *  isolate impact" instead of a number. Absent on payloads measured before
   *  the gate shipped. */
  affected_sample?: { n: number; min?: number; sufficient: boolean } | null;
  /** CUPED-adjusted win-rate effect (fraction) — the market-movement-adjusted
   *  estimate of THIS play's effect. Rendered in plain language inside the
   *  comparison details. Absent on payloads measured before CUPED shipped. */
  cuped_win_rate_effect?: number | null;
  /** The visibility twin of the above. The backend has always emitted it; the
   *  frontend type omitted it, so a visibility-register play could only ever
   *  render the WIN-RATE adjusted effect beside its visibility before/after —
   *  two metrics in one strip with nothing saying so. */
  cuped_visibility_effect?: number | null;
  model_version_changed?: boolean | null;
  attribution_caveat?: string;
  /** When this measurement was computed (ISO). It is refreshed on EVERY full
   *  benchmark run, which is why it belongs here and not on the playbook row —
   *  it dates the measurement, never the playbook. */
  measured_at?: string | null;
}

/** One shipped play's measured before/after — the portfolio per-play breakdown. */
export interface PlaybookPortfolioRow {
  playbook_id: string;
  title: string;
  playbook_type: string;
  register: 'visibility' | 'competitive';
  baseline: number | null;
  current: number | null;
  delta_pp: number | null;
  answers_recovered: number;
  improved: boolean;
}

/** Program-level closed-loop roll-up across shipped plays. */
/** What the whole playbook program EARNED — the portfolio twin of the per-play
 *  earned tier, same contract: counts and dates, no estimator, no cause claimed.
 *
 *  `pages_cited` against `pages_shipped` is the number the program is judged on.
 *  The pages not yet cited are "not yet", not "failed" — AI picks pages up on its
 *  own schedule, and a play can still be inside its typical window. */
export interface PlaybookPortfolioEarned {
  pages_shipped: number;
  pages_cited: number;
  citations_earned: number;
  positive_claims: number;
  negative_claims: number;
  citation_mix: PlaybookCitationMix | null;
}

/** One verb's ship → first-citation lag across every shipped play.
 *  Per VERB because the verbs do not pick up alike — this workspace reads
 *  promote 23.0d (n=6), reinforce 9.3d (n=1), create 3.8d (n=2). `n` is the
 *  population and must render with the median; only plays that earned a
 *  post-ship citation qualify, so it sits at or below the verb's shipped count. */
export interface PlaybookVerbLag {
  median_days: number | null;
  p25_days: number | null;
  p75_days: number | null;
  n: number;
}

export interface PlaybookPortfolio {
  shipped_count: number;
  measured_count: number;
  improved_count: number;
  avg_delta_pp: number | null;
  answers_recovered: number;
  plays: PlaybookPortfolioRow[];
  /** The EARNED receipts. Every field above this line comes from the
   *  uncontrolled before/after; these are counts off `source_citations` and
   *  `claims`. Keep them visually apart for the same reason the detail page
   *  does. */
  earned?: PlaybookPortfolioEarned | null;
  /** Keyed by verb; a verb with no qualifying play is absent, never a zero row. */
  pickup_lag_by_verb?: Record<string, PlaybookVerbLag>;
}

/** An issue this playbook was generated for / auto-linked to via
 *  `issue_playbook_links` — the reverse of "Suggested Fixes" ("Fixes N issues"). */
export interface LinkedIssueRow {
  /** Paid-search-equivalent demand behind the issue ($/mo). Null when unpriced. */
  demand_value_monthly?: number | null;
  id: string;
  title: string;
  issue_type: string;
  status: string;
  /** Explicit V2 attribution role; older payloads may omit it. */
  relationship?: 'assigned' | 'ripple';
  /** Canonical persona remains owned by the linked issue. */
  persona_key?: string | null;
  /** Evidence reach at the latest run that observed the issue. */
  latest_reach: number | null;
  /** ANY nonzero observation in the issue's history — a zero/blank latest_reach
   *  with this true renders the explained "not in latest run" state instead of
   *  an unexplained blank. Absent on older payloads. */
  ever_observed?: boolean;
  /** The unit latest_reach counts — the canonical per-lane reach basis
   *  ('answers' | 'citations' | 'pages' | …); absent on older payloads. */
  reach_basis?: EvidenceReachBasis | null;
  /** The issue's reach when this playbook shipped — the honest before number. */
  reach_at_ship: number | null;
  /** Normalized scope + competitor identity — Covers renders issues-list columns. */
  scope_label?: string | null;
  competitor_name?: string | null;
  competitor_domain?: string | null;
}

/** One verbatim AI quote in the receipts block (W2-G). Lane-shaped: a claim-lane
 *  quote carries claim/verdict/severity/source_url; a frozen judge quote carries
 *  sentiment. `evaluation_id` drills to the full answer. */
export interface PlaybookReceiptQuote {
  engine?: string | null;
  claim?: string | null;
  verbatim_quote?: string | null;
  verdict?: string | null;
  severity?: string | null;
  source_url?: string | null;
  sentiment?: string | null;
  evaluation_id?: string | null;
}

/** One losing prompt behind the play (W2-G) — decided losses first, each with the
 *  worst verbatim `quote` from that answer when one exists + the eval drill handle. */
export interface PlaybookLosingPromptReceipt {
  engine?: string | null;
  prompt?: string | null;
  outcome?: string | null;
  coverage?: string | null;
  competitor?: string | null;
  quote?: string | null;
  evaluation_id?: string | null;
}

/** Which quote layer supplied the receipts — names the provenance so the console
 *  can label it honestly. */
export type PlaybookQuotesSource = 'issue_claims' | 'frozen_judge_quotes' | 'losing_eval_claims';

/** The play's layered receipts (W2-G) — the SAME core assembly MCP `get_playbook`
 *  reads. The Plan tab renders these instead of the frozen-judge-quotes-only block. */
export interface PlaybookReceipts {
  verbatim_quotes: PlaybookReceiptQuote[];
  losing_prompts: PlaybookLosingPromptReceipt[];
  verbatim_quotes_source?: PlaybookQuotesSource | (string & {}) | null;
}

/** One ranked, econ-priced buyer term (W2-G) — from the audit `vocabulary_gaps`
 *  or the demand-priced `keyword_targets` fallback. Enriched with search
 *  economics + cited-page count when known; sparse otherwise (never a fake 0). */
export interface PlaybookBuyerTerm {
  term: string;
  monthly_volume?: number | null;
  cpc_usd?: number | null;
  cited_pages?: number | null;
  corpus_frequency?: number | null;
  top_cited_page?: string | null;
  intent?: boolean | null;
  demand?: string | null;
  /** Bins P1 (gap #3): the term is already present on the target page — shown as
   *  an "On page" ✓ for transparency, never a to-do. */
  on_page?: boolean | null;
}

/** Provenance of the server-assembled buyer terms. */
export type PlaybookBuyerTermsSource = 'vocabulary_gaps' | 'keyword_targets';

export interface ExecutionPlanAcceptanceCriterion {
  check: string;
  expected: string;
}

/** A customer-visible admitted statement. Opaque storage and locator fields are
 * intentionally omitted from the console type: the API may carry them, but the
 * reader never needs to render them. */
export interface ExecutionPlanReceipt {
  evidence_id: string;
  statement: string;
  source_url: string;
  provenance?: string | null;
  source_title?: string | null;
  published_at?: string | null;
  confidence?: string | null;
  independence?: string | null;
  about_competitor_name?: string | null;
}

export interface ExecutionPlanAuditReceipt {
  target_url: string;
  tactic_key: string;
  status: 'fail' | 'partial';
  finding: string;
}

export interface ExecutionPlanEvidenceAssessment {
  status: 'sufficient' | 'limited';
  offered_receipt_ids: string[];
  used_receipt_ids: string[];
  limitations: string[];
}

export interface ExecutionPlanResearchTask {
  claim_to_verify: string;
  required_sides: Array<'subject' | 'competitor'>;
  source_candidates: Array<{
    side: 'subject' | 'competitor';
    source_url: string;
    fetch_status?: string | null;
  }>;
  evidence_requirements: string[];
  completion_condition: string;
  pipeline_steps: Array<{
    stage: string;
    status: string;
    requirement: string;
    source_url?: string | null;
  }>;
}

export interface ClaimVerificationWarning {
  warning_id: string;
  claim: string;
  message: '⚠ Verify this claim is accurate before publishing.';
  reason: 'evidence_missing' | 'qualitative_receipt_does_not_establish_party_approach';
  related_receipt_ids?: string[];
}

interface ExecutionPlanBase {
  version: 1;
  verb: string;
  playbook_type: string;
  title: string;
  targeting_reason?: string | null;
  measurement_context?: {
    affected_answers?: number | null;
    affected_prompts?: number | null;
    covered_demand_monthly?: number | null;
    covered_demand_basis?: string | null;
    engines: string[];
    comparison?: {
      competitor?: string | null;
      wins: number;
      ties: number;
      losses: number;
      basis?: string | null;
    } | null;
    baseline_score?: number | null;
    page_citations?: number | null;
    source_citing_answers?: number | null;
    absent_or_loss_answers?: number | null;
    subject_wins?: number | null;
    listing_state?: string | null;
  } | null;
}

export interface ExecutionPlanStructuralGuidance {
  scope: 'page' | 'methodology';
  key: string;
  label: string;
  instruction: string;
  rationale?: string | null;
  status?: 'fail' | 'partial' | null;
  finding?: string | null;
}

export interface ExecutionPlanPublishingGuidance {
  suggested_author?: string | null;
  author_reason?: string | null;
  target_date?: string | null;
  target_date_reason?: string | null;
  primary_destination?: string | null;
  secondary_destinations: string[];
  placement_notes?: string | null;
  internal_links: Array<{
    from_url?: string | null;
    anchor_text?: string | null;
    to_url: string;
  }>;
}

export interface ExecutionPlanBuyerTermEconomics {
  term: string;
  monthly_volume: number | null;
  cpc_usd: number | null;
}

export interface ExecutionPlanContentBrief extends ExecutionPlanBase {
  deliverable: 'content_brief';
  verb: 'create';
  rationale: string;
  /** Versioned assignment presentation; absent on legacy briefs. */
  section_assignment_version?: 1 | 2 | 3 | 4 | 5;
  proposed_url?: string | null;
  page_metadata?: {
    title_tag?: string | null;
    meta_description?: string | null;
    h1?: string | null;
  } | null;
  audience: string[];
  sections: Array<{
    heading: string;
    intent?: string | null;
    instruction: string;
    receipts: ExecutionPlanReceipt[];
    /** Advisory only; never changes section readiness or playbook status. */
    verification_warnings?: ClaimVerificationWarning[];
    research_task?: ExecutionPlanResearchTask | null;
    readiness?: 'copy_ready' | 'research_required';
  }>;
  questions_to_answer: string[];
  comparison: Array<{
    dimension: string;
    wins: number;
    ties: number;
    losses: number;
    evidence_status: 'two_sided_exact' | 'research_gap';
  }>;
  buyer_terms: string[];
  /** Optional for compatibility with V2 plans authored before keyword economics. */
  buyer_term_economics?: ExecutionPlanBuyerTermEconomics[];
  structural_guidance?: ExecutionPlanStructuralGuidance[];
  schema_type?: string | null;
  publishing_guidance?: ExecutionPlanPublishingGuidance | null;
  evidence: ExecutionPlanEvidenceAssessment;
  acceptance_criteria: ExecutionPlanAcceptanceCriterion[];
}

/** A page-specific editorial handoff for an existing page, never a patch. */
export interface ExecutionPlanEditorialRefreshBrief extends Omit<ExecutionPlanContentBrief, 'deliverable' | 'verb' | 'proposed_url'> {
  deliverable: 'editorial_refresh_brief';
  verb: 'promote' | 'reinforce';
  proposed_url?: null;
  target_url: string;
  target_snapshot: {
    target_url: string;
    retrieved_at?: string | null;
    provenance: string;
  };
}

export interface ExecutionPlanPagePatch extends ExecutionPlanBase {
  deliverable: 'page_patch';
  verb: 'promote' | 'reinforce';
  rationale: string;
  target_url: string;
  target_snapshot: {
    target_url: string;
    retrieved_at?: string | null;
    provenance: string;
  };
  changes: Array<{
    operation:
      | 'insert_before'
      | 'insert_after'
      | 'replace'
      | 'delete'
      | 'update_metadata'
      | 'add_internal_link';
    locator: { kind: 'heading' | 'text' | 'metadata'; value: string };
    instruction: string;
    suggested_copy?: string | null;
    receipts: ExecutionPlanReceipt[];
    audit_receipt?: ExecutionPlanAuditReceipt | null;
  }>;
  /** Optional for compatibility with V2 plans authored before keyword economics. */
  buyer_term_economics?: ExecutionPlanBuyerTermEconomics[];
  structural_guidance?: ExecutionPlanStructuralGuidance[];
  publishing_guidance?: ExecutionPlanPublishingGuidance | null;
  evidence: ExecutionPlanEvidenceAssessment;
  acceptance_criteria: ExecutionPlanAcceptanceCriterion[];
}

export interface ExecutionPlanWorkOrder extends ExecutionPlanBase {
  deliverable: 'work_order';
  verb: 'fix';
  rationale: string;
  target: string;
  target_kind: 'url' | 'domain';
  steps: Array<{
    step_kind?: 'edit' | 'research' | 'verification';
    location?: string | null;
    instruction: string;
    rationale?: string | null;
    done_when: string;
    audit_receipt?: ExecutionPlanAuditReceipt | null;
  }>;
  verification: ExecutionPlanAcceptanceCriterion;
  publishing_guidance?: ExecutionPlanPublishingGuidance | null;
}

export interface ExecutionPlanOutreach extends ExecutionPlanBase {
  deliverable: 'outreach';
  verb: 'earn';
  rationale: string;
  target: string;
  target_kind: 'url' | 'domain';
  pitch_points: Array<{
    point: string;
    rationale?: string | null;
    receipts: ExecutionPlanReceipt[];
  }>;
  publishing_guidance?: ExecutionPlanPublishingGuidance | null;
  evidence: ExecutionPlanEvidenceAssessment;
  acceptance_criteria: ExecutionPlanAcceptanceCriterion[];
}

export interface ExecutionPlanNotAuthorable extends ExecutionPlanBase {
  deliverable: 'not_authorable';
  requested_deliverable: 'content_brief' | 'editorial_refresh_brief' | 'page_patch' | 'work_order' | 'outreach';
  explanation: string;
  target_url?: string | null;
  missing_requirements: string[];
  research_tasks: ExecutionPlanResearchTask[];
  handoff_type: 'refusal' | 'research_plan' | 'defer';
}

/** Closed V2 shapes the console can render as rich, typed sections. Unknown
 * schema revisions remain readable through the canonical server Markdown. */
export type ExecutionPlanOutcome =
  | ExecutionPlanContentBrief
  | ExecutionPlanEditorialRefreshBrief
  | ExecutionPlanPagePatch
  | ExecutionPlanWorkOrder
  | ExecutionPlanOutreach
  | ExecutionPlanNotAuthorable;

export interface ExecutionPlanSection {
  id: 'plan-decision' | 'plan-specification' | 'plan-publishing' | 'plan-evidence' | 'plan-completion';
  label: string;
  markdown: string;
}

export interface ExecutionPlanPagePatchDiagnostic {
  version: 1;
  severity: 'error' | 'warning';
  code:
    | 'exclusive_locator_collision'
    | 'internal_link_target_mutated'
    | 'duplicate_internal_link'
    | 'substring_locator_relation';
  locator_kind: 'heading' | 'text' | 'metadata';
  locator_sha256: string;
  related_locator_sha256?: string | null;
  operations: Array<
    | 'insert_before'
    | 'insert_after'
    | 'replace'
    | 'delete'
    | 'update_metadata'
    | 'add_internal_link'
  >;
  change_count: number;
}

export type ExecutionPlanCopyMaterialKind =
  | 'evidence_material'
  | 'validated_draft'
  | 'publication_ready';

export interface ExecutionPlanCopySidecar {
  version: 1;
  source_execution_plan_sha256: string;
  copies: Array<{
    change_index: number;
    text: string;
    kind: ExecutionPlanCopyMaterialKind;
    claim_receipt_bindings: Array<{
      statement_sha256: string;
      start: number;
      end: number;
      receipt_ids: string[];
    }>;
    validation_version: 1;
  }>;
  content_sha256: string;
}

/** The server-validated, surface-neutral execution-plan projection. Its
 * presence is the explicit per-row reader switch; consumers must not combine
 * it with legacy action_prompt/content_brief prose. */
export interface ExecutionPlanProjection {
  projection_version: 1;
  outcome: 'authored' | 'not_authorable';
  deliverable: string;
  requested_deliverable: string;
  /** Server-derived from the typed outcome; never inferred by the console. */
  delivery_state?: 'patch_ready' | 'editorial_ready' | 'content_brief' | 'research_only' | null;
  title: string;
  execution_plan: ExecutionPlanOutcome | Record<string, unknown>;
  markdown: string;
  /** Optional only for rolling-deploy compatibility with an older backend. */
  sections?: ExecutionPlanSection[] | null;
  content_sha256: string;
  /** Non-persisted diagnostics; optional while older backends are rolling. */
  patch_diagnostics?: ExecutionPlanPagePatchDiagnostic[];
  /** Versioned, hash-bound editorial state for exact page-patch copy material. */
  copy_sidecar?: ExecutionPlanCopySidecar | null;
}

export interface PlaybookV2Projection {
  schema_version: 2;
  execution: ExecutionPlanProjection;
  operational: {
    generation_key: string;
    origin: 'defend' | null;
    demand_value_monthly: number | null;
    demand_basis: 'priced' | 'imputed_topic_value' | null;
    measurement: {
      kind: 'citation' | 'prompts' | 'issue_recheck' | 'listing' | 'correction' | 'amplify';
      target_url: string | null;
      target_domain: string | null;
      target_url_hash: string | null;
      baseline_value: number | null;
    };
    topic_ids: string[];
    issues: Array<{
      id: string;
      relationship: 'assigned' | 'ripple';
      persona_key: string | null;
    }>;
  };
}

export interface PlaybookDetail extends PlaybookListItem {
  evidence: PlaybookEvidence;
  artifact: PlaybookArtifact;
  action_prompt: string | null;
  action_rationale: string | null;
  misrepresentations?: PlaybookMisrepresentation[] | null;
  measured_impact?: PlaybookMeasuredImpact | null;
  /** Buyer-demand payload (topics + real queries behind the play). Null when
   *  the play's prompts carry no topics. The shared canonical type. */
  demand?: PlaybookDemand | null;
  /** Rigor receipts for the credibility row (n_evals / engines / measured /
   *  confidence mix). The shared canonical type. Null on thin payloads. */
  provenance?: PlaybookProvenance | null;
  /** Issues this playbook was generated for — the "Fixes N issues" reverse link. */
  linked_issues: LinkedIssueRow[];
  /** Recipe-branched tracking contract (Tracking tab): the resolved recipe +
   *  the structured reason when the prompt resolver returned empty. Absent on
   *  payloads that predate the taxonomy cutover — fall back to the verb
   *  registry's recipe. */
  tracking?: PlaybookTracking | null;
  /** W5-A ripple impact for a DONE play — the observational "did it pay off"
   *  view (citations-over-runs + downstream issues affected). Null/absent on
   *  active rows or payloads that predate W5-A. */
  impact?: PlaybookImpact | null;
  /** W2-G — the play's layered verbatim receipts (linked-issue claims → frozen
   *  judge quotes → losing-eval claims). The Plan tab renders these instead of
   *  the frozen-judge-quotes-only block. Null when no quote-bearing evidence. */
  receipts?: PlaybookReceipts | null;
  /** W2-G — the ranked, econ-priced buyer terms from the persisted plan
   *  (vocabulary_gaps or the keyword_targets fallback). */
  buyer_terms?: PlaybookBuyerTerm[] | null;
  buyer_terms_source?: PlaybookBuyerTermsSource | (string & {}) | null;
  /** Explicit single-reader cutover payload. Null/absent means render the
   * legacy production playbook exactly as before. */
  execution_plan_projection?: ExecutionPlanProjection | null;
  /** Canonical execution + operational identity shared with MCP. */
  v2?: PlaybookV2Projection | null;
  /** Additive projection for consumers that do not traverse the typed plan. */
  verification_warnings?: ClaimVerificationWarning[] | null;
}

export interface ListPlaybooksParams {
  status?: PlaybookStatus[];
  /** System dismissal reasons used by reconciliation queues. */
  dismissed_reason?: string[];
  playbook_type?: PlaybookType[];
  run_id?: string[];
  /** Exact rows for a page-scoped deep link. Repeated query parameter. */
  playbook_id?: string[];
  product_id?: string;
  family?: PlaybookFamily;
  /** Drop the heavy JSONB blobs (evidence/artifact/plan detail) — use for a
   *  browse-only fetch (e.g. the Ignored-tab dismissed-rows query) that never
   *  opens the detail brief. */
  light?: boolean;
  /** Defer the expensive shipped-page citation receipts until after first paint. */
  include_citations?: boolean;
  /** Bounded first-paint page; omit for consumers that genuinely need every row. */
  limit?: number;
  offset?: number;
}

/** One benchmark's citing-answer count for a play's page. */
export interface PlaybookCitationSeriesPoint {
  run_date: string | null;
  cited_answer_count: number;
  pre_ship: boolean;
}

/** Deferred `POST .../playbooks/citations` receipt for one list row. */
export interface PlaybookCitationStamp {
  cited_answer_count: number | null;
  cited_answer_total: number | null;
  cited_answer_count_at_ship: number | null;
  citation_series?: PlaybookCitationSeriesPoint[];
  days_to_first_citation?: number | null;
}

/** Raw statuses backing the default Active and Done reads. Needs review asks
 * for dismissed rows separately and narrows them to the system `superseded`
 * reason; genuinely rejected work stays out of every customer queue. */
export const DEFAULT_LIST_STATUSES: PlaybookStatus[] = ['proposed', 'approved', 'in_progress', 'shipped', 'measured'];

/** Shared by the infinite list and its route-hover prefetch. */
export const PLAYBOOK_LIST_PAGE_SIZE = 50;

export function defaultPlaybookListParams(): ListPlaybooksParams {
  return {
    status: [...DEFAULT_LIST_STATUSES],
    include_citations: false,
    limit: PLAYBOOK_LIST_PAGE_SIZE,
  };
}

export const PLAYBOOK_TYPE_LABELS: Record<PlaybookType, string> = {
  comparison_page: 'Comparison page',
  feature_page: 'Capability page',
  correction: 'Correction',
  icp_content: 'ICP content',
  brand_attribution: 'Brand attribution',
  site_fix: 'Site fix',
  earned_mention: 'Earned mention',
  markdown_page: 'Markdown page',
};

/** Type label with the legacy fallback: known types map; an un-retyped legacy
 *  string (deploy window) renders humanized ("revise_existing_page" →
 *  "Revise existing page"), never blank, never a crash. */
export function playbookTypeLabel(type: PlaybookType | (string & {}) | null | undefined): string | null {
  if (!type) return null;
  const known = (PLAYBOOK_TYPE_LABELS as Record<string, string>)[type];
  if (known) return known;
  const raw = type.replace(/_/g, ' ').trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : null;
}

export const PLAYBOOK_STATUS_LABELS: Record<PlaybookStatus, string> = {
  proposed: 'Active',
  approved: 'Active',
  in_progress: 'Active',
  shipped: 'Done',
  measured: 'Done',
  dismissed: 'Ignored',
};

/** Collapse a raw playbook status to one of three user-facing buckets.
 *  Use this instead of enumerating raw statuses in UI logic. */
export function playbookStatusBucket(status: PlaybookStatus | string): 'active' | 'done' | 'ignored' {
  switch (status) {
    case 'shipped':
    case 'measured':
      return 'done';
    case 'dismissed':
      return 'ignored';
    default:
      return 'active';
  }
}

/** Normalize a playbook status bucket to a StatusIndicator category.
 *
 *  Active and done must not share a dot. They did — `success` and `completed`
 *  both resolve to `bg-success`, so on a list showing both buckets every row
 *  wore the identical teal and the column said nothing (Max, 2026-07-28:
 *  *"I'm seeing the bullet point to the left of the active and the done are
 *  both teal"*).
 *
 *  Live work keeps the colour; finished work goes NEUTRAL and recedes — the
 *  same thing the default sort says by sinking done rows to the bottom. Colour
 *  marks what still needs you (calm-pass rule 1). Note `info` was not an option:
 *  `--info` and `--success` are adjacent steps of one brand ramp
 *  (`--md-secondary-50` / `-60`), so they read as the same teal. */
export function playbookStatusCategory(status: PlaybookStatus | string): string {
  switch (playbookStatusBucket(status)) {
    case 'done':
    case 'ignored':
      return 'neutral';
    default:
      return 'success';
  }
}
